<script lang="ts">
  import type { BeadColor } from '../catalog/types';
  import type { Pattern } from '../pipeline/pipeline';
  import type { PdfMode } from '../export/pdf';
  import Modal from './Modal.svelte';

  /**
   * Dialog d'export PDF — options + barre de progression.
   *
   * Le générateur PDF est importé dynamiquement à la 1ère génération
   * pour ne pas embarquer jsPDF (~80kB gz) dans le bundle principal.
   *
   * **Style** : 100% classes globales (Modal + btn + field + alert +
   * seg + progress). Spécifique restant : la card "estimate" (résumé
   * pages/plaques/dimensions) et les titres de section interne.
   */

  type Props = {
    open: boolean;
    pattern: Pattern;
    palette: ReadonlyArray<BeadColor>;
    projectName: string;
    onCancel: () => void;
  };

  const { open, pattern, palette, projectName, onCancel }: Props = $props();

  let mode = $state<PdfMode>('color');
  let includeShoppingList = $state(true);
  let generating = $state(false);
  let progress = $state(0);
  let errorMsg = $state('');

  $effect(() => {
    if (open) {
      progress = 0;
      generating = false;
      errorMsg = '';
    }
  });

  let totalPlates = $derived(pattern.layout.cols * pattern.layout.rows);
  let totalPages = $derived(
    1 +
      (includeShoppingList ? 1 : 0) +
      (mode === 'both' ? totalPlates * 2 : totalPlates),
  );

  async function handleGenerate(): Promise<void> {
    if (generating) return;
    generating = true;
    progress = 0;
    errorMsg = '';
    try {
      const { exportPatternToPdf } = await import('../export/pdf');
      const blob = await exportPatternToPdf({
        pattern,
        palette,
        projectName,
        mode,
        includeShoppingList,
        onProgress: (p) => (progress = p),
      });
      const filename = `${(projectName || 'patron').replace(/[^a-zA-Z0-9-_]+/g, '-')}.pdf`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      onCancel();
    } catch (err) {
      errorMsg = err instanceof Error ? err.message : 'Erreur inconnue';
      generating = false;
    }
  }
</script>

<Modal
  {open}
  title="Exporter en PDF"
  maxWidth={520}
  onClose={() => !generating && onCancel()}
>
  <section class="ex-section">
    <h3 class="ex-section__title">Format des plaques</h3>
    <div class="seg" role="group" aria-label="Format">
      <button
        type="button"
        class="seg__item"
        class:seg__item--active={mode === 'color'}
        onclick={() => (mode = 'color')}
        disabled={generating}
        title="Cellules teintées + symbole en surimpression"
      >Couleur</button>
      <button
        type="button"
        class="seg__item"
        class:seg__item--active={mode === 'bw'}
        onclick={() => (mode = 'bw')}
        disabled={generating}
        title="Cellules blanches + symbole noir (économique imprimante)"
      >Noir & blanc</button>
      <button
        type="button"
        class="seg__item"
        class:seg__item--active={mode === 'both'}
        onclick={() => (mode = 'both')}
        disabled={generating}
        title="Section couleur puis section N&B (double épaisseur)"
      >Les deux</button>
    </div>
    <p class="hint">
      {#if mode === 'color'}
        Chaque cellule est teintée avec sa couleur. Les couleurs proches du blanc
        (faible contraste) reçoivent une bordure pour rester visibles.
      {:else if mode === 'bw'}
        Aucune cellule teintée — uniquement le symbole. Idéal pour économiser
        l'encre / impression noir & blanc.
      {:else}
        Imprime d'abord la version couleur, puis la version N&B. Double le
        nombre de pages.
      {/if}
    </p>
  </section>

  <section class="ex-section">
    <h3 class="ex-section__title">Contenu</h3>
    <label class="field field--inline">
      <input
        type="checkbox"
        class="input--check"
        bind:checked={includeShoppingList}
        disabled={generating}
      />
      <span class="field__label">Inclure la liste de courses (couleurs · quantités)</span>
    </label>
  </section>

  <section class="ex-section ex-estimate">
    <h3 class="ex-section__title">Aperçu</h3>
    <div class="ex-estimate__row">
      <span>{totalPages} page{totalPages > 1 ? 's' : ''}</span>
      <span class="ex-estimate__dot">·</span>
      <span>{totalPlates} plaque{totalPlates > 1 ? 's' : ''}</span>
      <span class="ex-estimate__dot">·</span>
      <span>{pattern.width} × {pattern.height} perles</span>
    </div>
  </section>

  {#if generating}
    <section class="ex-section">
      <h3 class="ex-section__title">Génération en cours…</h3>
      <div
        class="progress"
        role="progressbar"
        aria-valuenow={Math.round(progress * 100)}
        aria-valuemin="0"
        aria-valuemax="100"
      >
        <div class="progress__bar" style={`width: ${Math.round(progress * 100)}%`}></div>
      </div>
      <span class="progress__label">{Math.round(progress * 100)} %</span>
    </section>
  {/if}

  {#if errorMsg}
    <p class="alert alert--error">⚠ {errorMsg}</p>
  {/if}

  {#snippet actions()}
    <button type="button" class="btn btn--ghost" onclick={onCancel} disabled={generating}>
      Annuler
    </button>
    <button
      type="button"
      class="btn btn--primary"
      disabled={generating}
      onclick={handleGenerate}
    >{generating ? 'Génération…' : 'Télécharger le PDF'}</button>
  {/snippet}
</Modal>

<style>
  /* Patterns spécifiques export :
   * - Sections internes avec titre uppercase caption muted
   * - Carte estimate (résumé pages/plaques/dims sur fond surface)
   */

  .ex-section {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .ex-section__title {
    font-family: var(--font-body);
    font-size: var(--text-caption);
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--color-text-muted);
    margin: 0;
  }

  .ex-estimate {
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    padding: var(--space-2) var(--space-3);
  }
  .ex-estimate__row {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--text-small);
    flex-wrap: wrap;
  }
  .ex-estimate__dot {
    opacity: 0.4;
  }
</style>
